import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import VideoModal from '../../components/base/VideoModal';

export default function HomePage() {
  const [showVideo, setShowVideo] = useState(true);
  const [hasWatchedVideo, setHasWatchedVideo] = useState(false);

  useEffect(() => {
    // Check if user has already watched the video in this session
    const watched = sessionStorage.getItem('videoWatched');
    if (watched) {
      setShowVideo(false);
      setHasWatchedVideo(true);
    }
  }, []);

  const handleVideoClose = () => {
    setShowVideo(false);
    setHasWatchedVideo(true);
    sessionStorage.setItem('videoWatched', 'true');
  };

  const videoUrl = "https://paamintl.org/modvids/paamintrovideo.mp4";

  return (
    <div className="min-h-screen bg-white">
      <VideoModal 
        isOpen={showVideo} 
        onClose={handleVideoClose} 
        videoUrl={videoUrl}
      />
      
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(184, 20, 74, 0.7), rgba(184, 20, 74, 0.5)), url('https://readdy.ai/api/search-image?query=Majestic%20African%20apostolic%20ministry%20scene%20with%20golden%20divine%20light%20streaming%20from%20heaven%2C%20silhouettes%20of%20people%20worshipping%20with%20raised%20hands%2C%20cross%20prominently%20displayed%2C%20warm%20sunset%20colors%20blending%20with%20spiritual%20golden%20glow%2C%20cinematic%20composition%20showing%20God%5Cs%20presence%20and%20power%2C%20inspiring%20and%20reverent%20atmosphere%20with%20African%20cultural%20elements&width=1920&height=1080&seq=hero-bg-1&orientation=landscape')`
          }}
        ></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="w-full">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="block" style={{ fontFamily: 'Pacifico, serif' }}>Peter Akinnubi</span>
              <span className="block text-4xl md:text-6xl mt-2">Apostolic Ministry</span>
            </h1>
            
            <div className="text-2xl md:text-3xl font-semibold mb-8 text-yellow-300">
              Christ + Nothing = Enough
            </div>
            
            <p className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed">
              Beyond the noise of religion, discover a global family of Spirit-filled believers 
              unified in the doctrine of Christ. Join the Christ Family Network and experience 
              true fellowship, apostolic teaching, and transformative discipleship.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link 
                to="/join"
                className="bg-white text-[#b8144a] hover:bg-gray-100 px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Join CFN Today
              </Link>
              <Link 
                to="/training"
                className="border-2 border-white text-white hover:bg-white hover:text-[#b8144a] px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Start Free Training
              </Link>
            </div>
          </div>
        </div>
        
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
          <i className="ri-arrow-down-line text-2xl"></i>
        </div>
      </section>

      {/* The Call Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">The Call</h2>
              <div className="prose prose-lg text-gray-700">
                <p className="text-xl leading-relaxed mb-6">
                  In June 1993, God called His servant - Apostle Peter Akinnubi and said:
                </p>
                <blockquote className="text-2xl font-semibold text-[#b8144a] italic border-l-4 border-[#b8144a] pl-6 my-8">
                  "My son, I am going to use you as I used Moses..."
                </blockquote>
                <p className="text-lg leading-relaxed">
                  From that divine encounter, a ministry was born that would span continents, 
                  unite believers across nations, and establish a global network of disciples 
                  committed to the pure doctrine of Christ.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=African%20apostolic%20leader%20in%20prayer%20and%20worship%2C%20divine%20calling%20scene%20with%20golden%20light%20from%20heaven%2C%20spiritual%20encounter%20with%20God%2C%20powerful%20ministry%20moment%2C%20man%20of%20God%20receiving%20divine%20commission%2C%20biblical%20atmosphere%20with%20warm%20lighting%20and%20heavenly%20presence&width=600&height=400&seq=the-call-1&orientation=landscape"
                alt="The Divine Call"
                className="rounded-lg shadow-2xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision, Mission, Mandate */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Foundation</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built on divine revelation, guided by apostolic truth, and empowered by the Spirit
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Vision */}
            <div className="bg-gradient-to-br from-[#b8144a] to-[#d91e5b] text-white p-8 rounded-xl shadow-xl">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-6">
                <i className="ri-eye-line text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">The Vision</h3>
              <p className="text-lg leading-relaxed">
                Reintroducing Christ to the world by rightly dividing the word of truth for the 
                conversion of souls and building up the body of Christ for the work of ministry, 
                for the unity of the faith and knowledge of the Son of God as we prepare believers 
                for the second coming of our Master.
              </p>
            </div>

            {/* Mission */}
            <div className="bg-white border-2 border-[#b8144a] p-8 rounded-xl shadow-xl">
              <div className="w-16 h-16 bg-[#b8144a] rounded-full flex items-center justify-center mb-6">
                <i className="ri-compass-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#b8144a] mb-4">The Mission</h3>
              <p className="text-gray-700 leading-relaxed">
                We are a spirit-empowered community, committed to salvation by grace, holy living, 
                passionate evangelism, supernatural empowerment, sacrificial service, and loyalty 
                to leadership. We cultivate followership, promote Christ crucified, and embrace 
                ministry gifts, maintaining godly discipline, guided by sound doctrine, and 
                extending God's kingdom on earth.
              </p>
            </div>

            {/* Mandate */}
            <div className="bg-gray-900 text-white p-8 rounded-xl shadow-xl">
              <div className="w-16 h-16 bg-[#b8144a] rounded-full flex items-center justify-center mb-6">
                <i className="ri-flag-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">The Mandate</h3>
              <p className="text-lg leading-relaxed">
                To gather the ecclesia—the called-out ones—not around a personality or denomination, 
                but around Christ. To root them in the rock of revealed truth, building a habitation 
                of God through the Spirit, teaching the Spirit that gives life, bringing the Church 
                from the wilderness of mixture into the Promised Land of fullness in Christ.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Global Training Program */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Global%20online%20ministry%20training%20program%20with%20diverse%20students%20from%20different%20nations%20studying%20together%20via%20video%20conference%2C%20digital%20learning%20platform%20interface%2C%20apostolic%20curriculum%20materials%2C%20international%20discipleship%20network%2C%20modern%20technology%20meeting%20ancient%20biblical%20truth&width=600&height=400&seq=training-program-1&orientation=landscape"
                alt="Global Training Program"
                className="rounded-lg shadow-2xl object-cover w-full h-96"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Global Mandate Training Program
              </h2>
              <p className="text-xl text-gray-700 mb-6">
                Begin your journey with our free 60-day Global Mandate Training Program (GMTP) - 
                a robust online training program that leads to joining the PAAM Global Workforce 
                and becoming part of the Christ Family Network.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-[#b8144a] rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span className="text-lg">Apostolic curriculum and sound doctrine</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-[#b8144a] rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span className="text-lg">Global fellowship network</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-[#b8144a] rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span className="text-lg">Ministry empowerment and tools</span>
                </div>
              </div>
              <Link 
                to="/training"
                className="bg-[#b8144a] hover:bg-[#a01240] text-white px-8 py-4 rounded-full text-lg font-semibold transition-colors duration-200 inline-block whitespace-nowrap cursor-pointer"
              >
                Start Free Training
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Christ Family Network */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Christ Family Network</h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
            A global communion of disciples, not just attendees. Join believers worldwide 
            in authentic fellowship, apostolic teaching, and transformative discipleship.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-[#b8144a] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-heart-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Member Disciple</h3>
              <p className="text-gray-600">
                Be guided through sound apostolic teaching, online training, and live fellowships 
                that transform your walk with Christ.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-[#b8144a] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-team-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Member Discipler</h3>
              <p className="text-gray-600">
                Lead others into truth, light, and order with tools and mentorship to build 
                and grow a local CFN center anywhere in the world.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-[#b8144a] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-hand-heart-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Member Sponsor</h3>
              <p className="text-gray-600">
                Your giving becomes seed in God's global field - feeding, healing, discipling, 
                and raising saints for the Kingdom.
              </p>
            </div>
          </div>

          <Link 
            to="/cfn"
            className="bg-[#b8144a] hover:bg-[#a01240] text-white px-8 py-4 rounded-full text-lg font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Learn More About CFN
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
